import { type ChangeEvent, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Error, Button, Icon, IconButton } from 'shared/ui'
import { Typography } from 'shared/ui/Typography'
import { getAriaLabel } from 'shared/lib'
import { useReviewViewContext } from 'pages/reviews/context'
import { type ImageUploaderStore } from 'pages/reviews/ui/ImageUploader'
import styles from './styles.module.scss'

type IImageUploaderInputProps = {
  store: ImageUploaderStore
  label: string
  subtitle: string
  error: string
  isSquare?: boolean
}

export const ImageUploader = observer(
  ({ store, label, subtitle, error, isSquare }: IImageUploaderInputProps) => {
    const ref = useRef<HTMLInputElement>(null)
    const [fileName, setFileName] = useState('')
    const { setImageFile, imageUrl, imageError, removeImage, imageLoading } = store
    const { setActiveTab } = useReviewViewContext()
    const errorMessage = error || imageError

    const handleUploadClick = () => {
      if (ref.current) {
        ref.current.value = ''
        ref.current.click()
      }

      setActiveTab('appearance')
    }

    const handleClose = () => {
      void removeImage()
      setActiveTab('appearance')
      setFileName('')
    }

    const onUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]

      if (file) {
        setImageFile(file)
        setFileName(file.name)
      }
    }

    return (
      <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <div className={styles.logoWrap}>
            <div
              className={classNames(styles.logo, { [styles.square]: isSquare })}
              style={{ backgroundImage: `url(${imageUrl})` }}
            >
              {!imageUrl && (
                <Icon icon={'imageFilled'} color='var(--content-neutral-primary)' fontSize={16} />
              )}
            </div>
            <div className={styles.labelWrap}>
              <div className={styles.label}>
                <Typography ariaLabel={getAriaLabel('Logo label')} variant={'body-md-medium'}>
                  {label}
                </Typography>
              </div>
              {errorMessage ? (
                <Error
                  type='text'
                  error={errorMessage}
                  typographyProps={{
                    variant: 'body-md-regular',
                  }}
                />
              ) : (
                <Typography
                  ariaLabel={getAriaLabel('Logo Subtitle')}
                  variant={'body-md-regular'}
                  color='var(--content-primary-tertiary)'
                  className={styles.logoSubtitle}
                  ellipsis
                >
                  {imageUrl ? fileName : subtitle}
                </Typography>
              )}
            </div>
          </div>
          {!imageUrl ? (
            <Button
              text='Upload'
              icon='upload'
              size='medium'
              contained='secondary'
              onClick={handleUploadClick}
              className={styles.uploadButton}
              loading={imageLoading}
            />
          ) : (
            <IconButton
              ariaLabel={getAriaLabel('RemoveImage')}
              icon='close'
              size='medium'
              variant='icon'
              color='tertiary'
              className={styles.removeButton}
              tooltipProps={{
                label: 'Remove',
                placement: 'top',
              }}
              loading={imageLoading}
              onClick={handleClose}
            />
          )}
          <input
            hidden
            type={'file'}
            ref={ref}
            accept={'image/png, image/jpeg, image/jpg, image/webp'}
            onChange={onUploadChange}
          />
        </Box>
      </Box>
    )
  }
)
